import React, {
  ChangeEvent,
  useState,
  useEffect,
  useContext,
  useRef
} from "react";
import {
  FormControl,
  FormControlLabel,
  FormLabel,
  RadioGroup,
  TextField,
  Autocomplete,
  Alert,
  Stack,
  Checkbox,
  Tooltip
} from "@mui/material";
import API from "api";
import Loader from "components/shared/Loader/Loader";
import Button from "components/shared/Button/Button";
import Session from "helpers/session";
import { useTranslation } from "react-i18next";
import { AddEncounterEvent } from "helpers/variables";
import { GlobalContext } from "context/Provider";
import { EncounterTimerDisplay } from "components/shared/EncounterTimer/EncounterTimer";
import ReassignDeviceDialog from "./ReassignDeviceDialog";

export interface deviceInterface {
  serialnumber: string;
  user: string;
  type: string;
  assigned: boolean;
  orderDate: boolean;
  lot: string;
}

interface AttributeProps {
  memberId?: string;
}

export default function Devices({memberId = ""}: AttributeProps) {
  const memberID = window.location.pathname.split("/").pop();
  const alertTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { encounterTimer, provider, member } = useContext(GlobalContext);
  const [value, setValue] = useState<string>();
  const [showAllDevices, setShowAllDevices] = useState<boolean>(false);
  const [allDevices, setAllDevices] = useState<deviceInterface[]>([]);
  const [devices, setDevices] = useState<deviceInterface[]>([]);
  const [waiting, setWaiting] = useState<boolean>(true);
  const [ImeI, setImeI] = useState<string>("");
  const [isValidImeI, setIsValidImeI] = useState(true);
  const [alertMessage, setAlertMessage] = useState<string>();
  const [showSuccess, setSuccess] = useState<string>("");
  const [selectedAddDevices, setSelectedAddDevices] = useState<deviceInterface[]>([]);
  const [selectedRemoveDevices, setSelectedRemoveDevices] = useState<deviceInterface[]>([]);
  const [reassignTarget, setReassignTarget] = useState<deviceInterface | null>(null);
  const [resolvedNames, setResolvedNames] = useState<Record<string, string>>({});
  const { t } = useTranslation();
  const isProvider =
    provider?.loggedInClient?.roles?.split(",").includes("Provider") ?? false;
  const isAssignedToCurrent = (device: deviceInterface) =>
    device.assigned && device.user === memberID;
  const isAssignedToOther = (device: deviceInterface) =>
    device.assigned && !!device.user && device.user !== memberID;
  const isUnassigned = (device: deviceInterface) => !device.assigned;
  const stagedSerials = new Set(
    selectedAddDevices.map((device) => device.serialnumber)
  );
  const addOptions = allDevices.filter((device) => {
    if (stagedSerials.has(device.serialnumber)) return false;
    if (isAssignedToCurrent(device)) return false;
    if (isUnassigned(device)) return true;
    if (isAssignedToOther(device)) return showAllDevices;
    return false;
  });
  const isButtonDisabled =
    selectedAddDevices.length === 0 &&
    selectedRemoveDevices.length === 0 &&
    ImeI.trim() === "";

  const buttonLabel = (() => {
    switch (true) {
      case selectedAddDevices.length > 0 && selectedRemoveDevices.length > 0:
        return "Add & Remove Devices";

      case selectedAddDevices.length > 0:
        return "Add Device(s)";

      case selectedRemoveDevices.length > 0:
        return "Remove Device(s)";

      default:
        return "No Devices Selected";
    }
  })();

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setValue(event.target.value);
    setSuccess("");
  };

  const helperTextRender = () => {
    return isValidImeI ? "" : t("s-p-devices-message-1");
  };

  const getDeviceList = () => {
    API.kore.devices
      .getAssignedInventory(
        Session.crackCookieToken("coordinator"),
        "cellular",
        memberID || ""
      )
      .then((D) => {
        setWaiting(false);
        if (D) {
          setDevices(D.items);
        } else {
          setDevices([]);
        }
      })
      .catch((deviceErr) => console.log(deviceErr));
    API.kore.devices
      .getAll(
        Session.crackCookieToken("coordinator"),
        "cellular"
      )
      .then((deviceListRet) => {
        setAllDevices(deviceListRet.items ?? []);
      })
      .catch((deviceErr) => console.log(deviceErr));
  };

  const getMemberNames = () => {
    API.kore.members
      .getAll(Session.crackCookieToken("coordinator"))
      .then((res: any) => {
        const map: Record<string, string> = {};
        (res?.items ?? []).forEach((member: any) => {
          map[member.id] =
            `${member.firstName ?? ""} ${member.lastName ?? ""}`.trim() ||
            member.username ||
            "";
        });
        setResolvedNames(map);
      })
      .catch((e: any) => console.log(e));
  };

  const ownerDisplayLabel = (device: deviceInterface | null) => {
    if (!device) return "";
    return resolvedNames[device.user] || device.user;
  };

  const ownerShortLabel = (device: deviceInterface) => {
    const name = resolvedNames[device.user];
    if (!name) return "";
    const [first = "", second = ""] = name.split(" ");
    return (first[0] ?? "").toUpperCase() + (second[0] ?? "").toUpperCase();
  };

  const handleConfirmReassign = () => {
    if (!reassignTarget) return;

    if (!isProvider && isAssignedToOther(reassignTarget) && !ownerIsOnRoster(reassignTarget)) {
      showAlert(
        "You do not have access to reassign this device because the member it's assigned to is not assigned to you."
      );
      setReassignTarget(null);
      return;
    }

    setSelectedAddDevices((prev) =>
      prev.some((device) => device.serialnumber === reassignTarget.serialnumber)
        ? prev
        : [...prev, reassignTarget]
    );
    setReassignTarget(null);
  };

  const ownerIsOnRoster = (device: deviceInterface) => {
    const myMemberIds = new Set(
      (member?.list ?? []).map((rosterMember: any) => rosterMember.id)
    );
    return myMemberIds.has(device.user);
  };

  const logCurrentMemberEncounter = (
    adds: string[],
    removes: string[],
    reassignPairs: { serial: string; from: string }[]
  ) => {
    if (!adds.length && !removes.length && !reassignPairs.length) return;

    const noteLines = [
      ...adds.map((serial) => `Device ${serial} assigned.`),
      ...removes.map((serial) => `Device ${serial} unassigned.`),
      ...reassignPairs.map(
        ({ serial, from }) =>
          `Device ${serial} unassigned from ${from} and reassigned to ${memberID}.`
      )
    ];

    const encounterEvent = AddEncounterEvent("profile change", noteLines.join("\n"));
    const duration = encounterTimer?.current?.duration ?? 0;
    Session.updateSessionEncounter(
      window.sessionStorage.getItem("encounter") || "",
      encounterEvent,
      duration.toString()
    );
  };

  const logSourceMemberEncounters = async (
    pairs: { serial: string; from: string }[],
    buildNote: (serial: string, from: string) => string
  ) => {
    if (pairs.length === 0) return;

    const bySource: Record<string, string[]> = {};
    pairs.forEach(({ serial, from }) => {
      (bySource[from] ??= []).push(serial);
    });

    await Promise.allSettled(
      Object.entries(bySource).map(([sourceMemberId, serials]) =>
        API.kore.encounters.add(Session.crackCookieToken("coordinator"), {
          coordinatorId: Session.crackCookieUserId("coordinator"),
          coordinatorName: Session.crackCookieUsername("coordinator"),
          memberId: sourceMemberId,
          memberName: resolvedNames[sourceMemberId] || "",
          duration: "0",
          actions: serials.map((serial) =>
            AddEncounterEvent("profile change", buildNote(serial, sourceMemberId))
          )
        })
      )
    );
  };

  const executeReassign = (device: deviceInterface) => async () => {
    const serial = device.serialnumber.toLowerCase();
    const token = Session.crackCookieToken("coordinator");

    const removeRes = await API.kore.devices.removeCellDevice(token, serial, device.user);
    if ([409, 500].includes(removeRes?.response?.status)) throw new Error("unassign_failed");

    const addRes = await API.kore.devices.addCellDevice(token, serial, memberID || "");
    if ([409, 500].includes(addRes?.response?.status)) throw new Error("failed_reassign");

    return serial;
  };

  const submissionClicked = async () => {
    if (!isProvider) {
      const blocked = selectedAddDevices.filter(
        (device) => isAssignedToOther(device) && !ownerIsOnRoster(device)
      );
      if (blocked.length > 0) {
        showAlert(
          "One or more devices could not be reassigned because their assigned member is not assigned to you."
        );
        setWaiting(false);
        return;
      }
    }

    const reassigns = selectedAddDevices.filter((device) => isAssignedToOther(device));
    const plainAddDevices = selectedAddDevices.filter((device) => !isAssignedToOther(device));

    const plainAddSerials: string[] = [
      ...(ImeI ? [ImeI.toLowerCase()] : []),
      ...plainAddDevices.map((device) => device.serialnumber.toLowerCase())
    ];
    const removeSerials: string[] = selectedRemoveDevices.map((device) =>
      device.serialnumber.toLowerCase()
    );

    if (
      plainAddSerials.length === 0 &&
      removeSerials.length === 0 &&
      reassigns.length === 0
    ) {
      window.confirm(t("s-p-devices-message-14"));
      console.groupEnd();
      return;
    }

    const addCountForPrompt = plainAddSerials.length + reassigns.length;
    const allAddSerialsForPrompt = [
      ...plainAddSerials,
      ...reassigns.map((device) => device.serialnumber.toLowerCase())
    ];

    const confirmationMessage =
      addCountForPrompt > 0 && removeSerials.length > 0
        ? `${t("s-p-devices-message-2")} ${allAddSerialsForPrompt.join(", ")} ${t(
          "s-p-devices-message-3"
        )} ${removeSerials.join(", ")}?`
        : addCountForPrompt > 0
          ? `${t("s-p-devices-message-8")} ${allAddSerialsForPrompt.join(", ")}?`
          : `${t("s-p-devices-message-13")} ${removeSerials.join(", ")}?`;

    if (!window.confirm(confirmationMessage)) {
      console.groupEnd();
      return;
    }

    setWaiting(true);

    const parallelRequests = [
      ...plainAddSerials.map((serial) => ({
        device: serial,
        type: "add" as const,
        promise: API.kore.devices.addCellDevice(
          Session.crackCookieToken("coordinator"),
          serial,
          memberID || ""
        )
      })),
      ...removeSerials.map((serial) => ({
        device: serial,
        type: "remove" as const,
        promise: API.kore.devices.removeCellDevice(
          Session.crackCookieToken("coordinator"),
          serial,
          memberID || ""
        )
      }))
    ];

    const reassignResults = await Promise.allSettled(
      reassigns.map((device) => executeReassign(device)())
    );

    const addRemoveResults = await Promise.allSettled(
      parallelRequests.map((request) => request.promise)
    );

    let failures: string[] = [];
    let successfulAdds: string[] = [];
    let successfulRemovals: string[] = [];
    let strandedDevices: { serial: string; from: string }[] = [];
    let successfulReassignPairs: { serial: string; from: string }[] = [];

    addRemoveResults.forEach((result, index) => {
      const meta = parallelRequests[index];

      if (result.status === "rejected") {
        failures.push(`${meta.device}: request failed`);
        return;
      }

      const status = result.value?.response?.status;
      const text = result.value?.response?.text;

      if (status === 409 || status === 500) {
        let message = "Logical failure";
        try {
          message = JSON.parse(text || "{}").message || message;
        } catch {}
        failures.push(`${meta.device}: ${message}`);
        return;
      }

      if (meta.type === "add") successfulAdds.push(meta.device);
      else successfulRemovals.push(meta.device);
    });

    reassignResults.forEach((result, index) => {
      const device = reassigns[index];
      const serial = device.serialnumber.toLowerCase();
      if (result.status === "rejected") {
        if (result.reason?.message === "failed_reassign") {
          strandedDevices.push({ serial, from: device.user });
          failures.push(`${serial}: unassigned but NOT reassigned`);
        } else {
          failures.push(`${serial}: reassign failed`);
        }
      } else {
        successfulReassignPairs.push({ serial, from: device.user });
      }
    });

    if (failures.length === 0) setSuccess("success");
    else setSuccess("failure");

    if (strandedDevices.length > 0) {
      const strandedDevicesSerials = strandedDevices.map((strandedDevice) => strandedDevice.serial);
      showAlert(
        `Warning: ${strandedDevicesSerials.join(", ")} ${
          strandedDevicesSerials.length === 1 ? "was" : "were"
        } removed from the previous member but could not be assigned. Please retry the assignment.`
      );
    }

    // Add encounter events to current member and create encounters for members device reassigned from
    logCurrentMemberEncounter(successfulAdds, successfulRemovals, successfulReassignPairs);
    await logSourceMemberEncounters(
      successfulReassignPairs,
      (serial, from) =>
        `device ${serial} unassigned from ${from} and reassigned to ${memberID}`
    );
    await logSourceMemberEncounters(
      strandedDevices,
      (serial, from) =>
        `device ${serial} unassigned from ${from} (reassignment failed; device is currently unassigned)`
    );

    setImeI("");
    setSelectedAddDevices([]);
    setSelectedRemoveDevices([]);
    setWaiting(false);
    getDeviceList();

    setTimeout(() => {
      setSuccess("");
      console.groupEnd();
    }, 4000);
    setTimeout(() => {
      getDeviceList();
    }, 500);
  };

  const showAlert = (msg: string) => {
    if (alertTimer.current) clearTimeout(alertTimer.current);
    setAlertMessage(msg);
    alertTimer.current = setTimeout(() => setAlertMessage(""), 15000);
  };

  useEffect(() => {
    window.scroll({
      top: 172,
      left: 0,
      behavior: "smooth"
    });
    getDeviceList();
    getMemberNames();
  }, []);

  return (
    <Stack
      sx={{
        overflow: "auto",
        position: "fixed",
        top: 290,
        bottom: 68,
        left: 160,
        right: 200,
        paddingBottom: 5,
        paddingLeft: 25,
        paddingRight: 5
      }}
    >
      <EncounterTimerDisplay/>
      <Stack sx={{ paddingLeft: 10, paddingRight: 4 }} spacing={3}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <h1 style={{ margin: 0 }}>{t("u-connected-cellular-devices")}</h1>
          <Tooltip title="Displays devices assigned to other members">
            <FormControlLabel
              control={
                <Checkbox
                  checked={showAllDevices}
                  onChange={(e) => setShowAllDevices(e.target.checked)}
                />
              }
              label="Show all devices"
            />
          </Tooltip>
        </Stack>
        <h5>
          Select dropdown menus below to manage device assignment. Multiple devices can be added or removed at the same time.
        </h5>
        <FormControl component="fieldset">
          <FormLabel style={{ paddingBottom: 10 }} component="legend">{t("u-device-by-imei")}</FormLabel>
          {alertMessage ? (
            <h3
              style={{
                fontFamily: "helvetica neue",
                color: "white",
                backgroundColor: "red",
                borderRadius: "3px",
                padding: "10px 100px"
              }}
            >
              {alertMessage}
            </h3>
          ) : (
            ""
          )}
          <RadioGroup
            aria-label="gender"
            name="device"
            value={value}
            onChange={handleChange}
          >
            {waiting ? (
              <Loader />
            ) : devices.length > 0 ? (
              <Autocomplete
                multiple
                id="remove-devices"
                options={devices}
                getOptionLabel={(device) => device.serialnumber.toUpperCase()}
                value={selectedRemoveDevices}
                onChange={(event, newValue) => setSelectedRemoveDevices(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    label="Remove Devices"
                    placeholder="Select devices to remove"
                  />
                )}
                sx={{ minWidth: 250 }}
              />
            ) : (
              <div>
                <p
                  style={{
                    color: "red",
                    border: "2px solid red",
                    borderRadius: "6px",
                    padding: "6px"
                  }}
                >
                  {t("s-no-cellulare-devices")}
                </p>
              </div>
            )}
          </RadioGroup>
        </FormControl>
        <Autocomplete
          multiple
          id="add-devices"
          options={addOptions}
          getOptionLabel={(device) => device.serialnumber.toUpperCase()}
          value={selectedAddDevices}
          onChange={(event, newValue) => setSelectedAddDevices(newValue)}
          renderOption={(props, option) => {
            const other = isAssignedToOther(option);
            const { key, onClick, ...rest } = props as any;

            if (other) {
              const initials = ownerShortLabel(option);
              return (
                <li
                  {...rest}
                  key={option.serialnumber}
                  style={{ color: "rgba(0, 0, 0, 0.38)" }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setReassignTarget(option);
                  }}
                >
                  {option.serialnumber.toUpperCase()}
                  {initials && (
                    <span style={{ paddingLeft: 8 }}>— {initials}</span>
                  )}
                </li>
              );
            }
            return (
              <li {...rest} key={option.serialnumber} onClick={onClick}>
                {option.serialnumber.toUpperCase()}
              </li>
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              label="Add Devices"
              placeholder="Select devices to add"
              helperText={helperTextRender()}
              error={!isValidImeI}
            />
          )}
          sx={{ minWidth: 250 }}
        />
        <Button
          variant={"success"}
          onClick={submissionClicked}
          disabled={isButtonDisabled}
        >
          {buttonLabel}
        </Button>
        {showSuccess === "success" && (
          <Alert severity="success">
            Your configurations have been saved.
          </Alert>
        )}
        {showSuccess === "failure" && (
          <Alert severity="error">
            One or more device updates failed. Please try again.
          </Alert>
        )}
      </Stack>
      <ReassignDeviceDialog
        device={reassignTarget}
        ownerName={ownerDisplayLabel(reassignTarget)}
        onConfirm={handleConfirmReassign}
        onCancel={() => setReassignTarget(null)}
      />
    </Stack>
  );
}
